import { z } from 'zod';

import { Draw } from '../loan/Draw';
import { BalancePaymentSnapshot } from '../loan/Snapshots';

// Our API fields deviate slightly from what we use internally.
// When we version our API, we may need to version this type as well.

export const TransactionForApi = z.object({
  id: z.string(),
  losId: z
    .string()
    .describe(
      'LOS ID of the loan the transaction occurred on. This LOS ID is provided by the client during loan creation.',
    ),
  externalId: z
    .string()
    .optional()
    .describe(
      'Optional unique ID provided with transactions posted via the API. Maps to idempotencyKey from POST requests.',
    ),
  type: z.enum([
    'PAYMENT',
    'PAYMENT_REVERSAL',
    'DISBURSEMENT',
    'DISBURSEMENT_REVERSAL',
    'SWEEP',
    'DRAW',
    'DRAW_REVERSAL',
    'PAYOFF',
    'PARTIAL_PAYOFF',
    'PAYOFF_REVERSAL',
    'BOARDING',
    'FEE',
    'FEE_REVERSAL',
    'PAYMENT_DEFERMENT',
    'PAYMENT_DEFERMENT_REVERSAL',
    'PAYMENT_DEFERRAL',
    'PAYMENT_DEFERRAL_REVERSAL',
    'DEPOSIT',
    'DEPOSIT_REVERSAL',
    'PRINCIPAL_MODIFICATION',
    'PRINCIPAL_MODIFICATION_REVERSAL',
    'WRITEOFF',
    'LIQUIDATION',
    'LIQUIDATION_REVERSAL',
  ]),
  status: z.enum(['PENDING', 'PROCESSED', 'FAILED', 'CANCELLED']),
  disbursementType: z
    .enum(['SERVICING_TRANSFER', 'ESCROW', 'FORWARD_PAYMENT', 'ADVANCE', 'PAYOFF', 'PAYMENT_AFTER_PAYOFF', 'OTHER'])
    .optional()
    .describe('Type of disbursement. Will be null when type != DISBURSEMENT'),
  dateDue: z.iso
    .datetime()
    .optional()
    .describe('Date that the transaction was due if it was allocated toward a statement'),
  processedDate: z.iso.datetime().optional().describe('Date that the transaction was processed'),
  dateReceived: z.iso.datetime().optional().describe('Date that the transaction was received'),
  method: z
    .enum([
      'CHECK',
      'WIRE',
      'PHONE',
      'ACH',
      'CREDIT_CARD',
      'DEBIT_CARD',
      'AUTHORIZED_ACH',
      'RESERVE',
      'BALANCE_FORWARD',
      'PRINCIPAL_PREPAYMENT',
      'BALANCE_REALLOCATION',
      'LOCKBOX',
    ])
    .optional(),
  referenceId: z
    .string()
    .optional()
    .describe(
      'Provides contextual information related to the type of transaction. When type = PAYMENT and method = CHECK: referenceId is the check # recorded by lender. When type = PAYMENT and method = ACH: referenceId is the corresponding id of the payment transfer from the payment processor. When type = PAYMENT_REVERSAL: referenceId is the ID of payment that was reversed. When type = DISBURSEMENT_REVERSAL: referenceId is the ID of disbursement that was reversed.',
    ),
  expectedPaymentAmount: z.string().describe('String in monetary format (0.00)'),
  amount: z
    .string()
    .describe(
      'Net cash movement of the transaction in monetary format (0.00). This is 0.00 for balance reallocations and reversals to unapplied suspense, where funds move between buckets without net cash changing hands. See grossTransactionAmount for the gross magnitude of funds moved.',
    ),
  grossTransactionAmount: z
    .string()
    .describe(
      'The gross magnitude of funds a transaction moved, in monetary format (0.00), as opposed to its net external cash movement (amount), which nets to 0.00 for balance reallocations, balance transfers, and reversals to unapplied suspense. For example, a $500 holdback→escrow reallocation reports a gross of 500.00 while its amount is 0.00.',
    ),
  amountPrincipal: z.string().describe('String in monetary format (0.00)'),
  amountPrincipalPrepayment: z.string().describe('String in monetary format (0.00)'),
  amountInterest: z.string().describe('String in monetary format (0.00)'),
  amountEscrow: z.string().describe('String in monetary format (0.00)'),
  amountFee: z.string().describe('String in monetary format (0.00)'),
  amountSuspense: z.string().describe('String in monetary format (0.00)'),
  amountUnappliedSuspense: z.string().describe('String in monetary format (0.00)'),
  amountAdvance: z.string().describe('String in monetary format (0.00)'),
  balanceSnapshot: BalancePaymentSnapshot,
  achBatch: z.string().optional().describe('ACH Batch id. Only applicable for authorized ACH payments'),
  disbursementRecipient: z.string().optional().describe('null when type != DISBURSEMENT'),
  comment: z.string().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TransactionForApi = z.infer<typeof TransactionForApi>;

// TODO: Add more transaction types to this union as we support posting them
export type TransactionForApiPostPutType = Draw;
